/* Top-bar Purchase Order approvals inbox (beside the maintenance inbox).
   Mirrors MaintenanceApprovals: the SERVER decides who is an approver
   (purchase.approve + eligibility at the PO's current level); a 403 hides the
   button and stops polling. Clicking a row jumps to the Purchase page. */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ShoppingCart } from "lucide-react";

const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function POApprovals() {
  const { user, selectedUnit } = useAuth();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(false);
  const deniedRef = useRef(false);
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      // Scope the inbox to the ACTIVE unit — a multi-unit approver only sees the
      // selected unit's pending POs (the backend enforces this filter too).
      const qs = selectedUnit?.id ? `?unit_id=${selectedUnit.id}` : "";
      const r = await api.get(`/purchase-orders/pending-approvals${qs}`, { silent403: true });
      deniedRef.current = false;
      setAllowed(true);
      setRows(r.data.records || []);
      setCount(r.data.count || 0);
    } catch (e) {
      if (e.response?.status === 403) { deniedRef.current = true; setAllowed(false); }
    }
  }, [selectedUnit?.id]);

  useEffect(() => {
    if (!user) return;
    deniedRef.current = false;
    load();
    const t = setInterval(() => { if (!deniedRef.current) load(); }, 120000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, selectedUnit?.id]);

  if (!allowed) return null;

  return (
    <>
      <Button variant="ghost" size="icon" className="relative mr-1"
              onClick={() => { setOpen(true); load(); }}
              title="Purchase orders waiting for your approval"
              data-testid="po-approvals-btn">
        <ShoppingCart className="w-5 h-5" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-sky-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
                data-testid="po-approvals-count-badge">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-sky-600" />
              Purchase order approvals ({count} pending)
            </DialogTitle>
          </DialogHeader>
          {!rows.length ? (
            <p className="text-sm text-muted-foreground py-4">
              Nothing waiting — every purchase order at your level is decided.
            </p>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <button key={r.id} type="button"
                        className="w-full text-left rounded-lg border p-3 text-xs space-y-1 hover:bg-accent/50 transition-colors"
                        onClick={() => { setOpen(false); navigate("/purchase"); }}
                        data-testid={`po-approval-row-${r.po_no}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-semibold">{r.po_no} · {r.party_name}</span>
                    <span className="font-semibold">{inr(r.total_amount)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Badge className="text-[10px] bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400 uppercase">
                      {(r.po_type || "").replace("_", " ")}
                    </Badge>
                    <span>Level {r.current_level}/{r.levels_total} · {r.level_label}</span>
                    <span className="ml-auto">raised by {r.created_by_name || "—"} · {r.po_date}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
